/**
 * ``TemplatePreview`` — 4:3 PNG preview for a presentation template.
 *
 * The BE renders previews via ``headless_office`` and stores the PNG in
 * MinIO; the proxy route ``GET /api/v1/presentation-templates/_preview/{key}``
 * streams it back. We render it as a plain ``<img>`` (PNG, not interactive)
 * inside a 4:3 rounded container with a hairline border.
 *
 * Why an ``<img>`` and not an ``<iframe>``: the BE response is a single
 * raster asset, not an HTML document. An iframe would only add an extra
 * browsing context for no benefit and complicate sandboxing. If/when a Wave
 * 3 live-HTML preview lands, this component is the swap point — the call
 * sites and the API contract (``previewUrl``) stay identical.
 *
 * Refresh state: the editor passes a ``isRefreshing`` flag (toggled while a
 * PATCH is in flight); we overlay an "Aggiornando…" badge so the user
 * understands the thumbnail lags behind the form by ~1 render cycle.
 */

import type * as React from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { Sparkles } from "lucide-react";

import { cn } from "@ando/ui";

export interface TemplatePreviewProps {
  /**
   * BE-proxied PNG URL — typically the ``preview_url`` from a
   * ``TemplateDetail`` / ``TemplateSummary``. ``null`` if no preview exists
   * yet (e.g. just-created custom template before the async render job runs).
   */
  readonly previewUrl: string | null;
  /** Display name — used as the ``alt`` text. */
  readonly name: string;
  /** Shows the "Aggiornando…" overlay while a refetch is in flight. */
  readonly isRefreshing?: boolean;
  /** Hint shown below the preview frame. */
  readonly caption?: string;
  /** Extra className appended to the root container. */
  readonly className?: string;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  previewUrl,
  name,
  isRefreshing = false,
  caption,
  className,
}) => {
  const { t } = useLingui();
  return (
    <figure className={cn("flex w-full flex-col gap-2", className)}>
      <div
        className={cn(
          "relative aspect-[4/3] w-full overflow-hidden rounded-md border border-hairline bg-gradient-to-br from-ando-orange-soft/25 to-amber-500/10",
        )}
        data-testid="template-preview-frame"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t`Anteprima del template ${name}`}
            className="h-full w-full object-contain"
            // Avoid Next-style optimization (we're in a Vite SPA); plain
            // <img> + lazy-load is correct here.
            loading="lazy"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-ink-3">
            <Sparkles size={28} aria-hidden />
            <span className="text-[12px]">
              <Trans>Anteprima non ancora generata</Trans>
            </span>
          </div>
        )}

        {isRefreshing ? (
          <div
            role="status"
            aria-live="polite"
            className="absolute inset-0 flex items-center justify-center bg-card/70 backdrop-blur-sm"
          >
            <span className="rounded-full bg-ink-1/90 px-3 py-1 font-medium text-[12px] text-white shadow-sm">
              <Trans>Aggiornando…</Trans>
            </span>
          </div>
        ) : null}
      </div>

      {caption ? <figcaption className="text-[11.5px] text-ink-3">{caption}</figcaption> : null}
    </figure>
  );
};
TemplatePreview.displayName = "TemplatePreview";
